import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AddressManagementSection = ({ profile, onSave, saving }) => {
  const [addresses, setAddresses] = useState([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    locality: '',
    city: '',
    state: '',
    pinCode: '',
    country: '',
    isPrimary: false
  });

  // Normalize incoming addresses from profile (supports legacy `street`/`zipCode` and new `locality`/`pin_code`)
  const normalizeAddress = (a) => ({
    id: a?.id,
    label: a?.label || '',
    locality: a?.locality ?? a?.street ?? '',
    city: a?.city ?? '',
    state: a?.state ?? '',
    pinCode: a?.pin_code ?? a?.pinCode ?? a?.zipCode ?? '',
    country: a?.country ?? '',
    isPrimary: a?.is_primary ?? a?.isPrimary ?? false
  });

  useEffect(() => {
    if (profile?.addresses) {
      setAddresses(profile?.addresses?.map(normalizeAddress));
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingIndex(null);
    setFormData({
      label: '',
      locality: '',
      city: '',
      state: '',
      pinCode: '',
      country: '',
      isPrimary: addresses?.length === 0
    });
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setIsAddingNew(false);
    setFormData(addresses?.[index] ? normalizeAddress(addresses?.[index]) : {
      label: '',
      locality: '',
      city: '',
      state: '',
      pinCode: '',
      country: '',
      isPrimary: false
    });
  };

  const handleCancel = () => {
    setIsAddingNew(false);
    setEditingIndex(null);
    setFormData({
      label: '',
      locality: '',
      city: '',
      state: '',
      pinCode: '',
      country: '',
      isPrimary: false
    });
  };

  const handleSave = () => {
    let updatedAddresses = [...addresses];
    
    if (formData?.isPrimary) {
      updatedAddresses = updatedAddresses?.map((addr) => ({
        ...addr,
        isPrimary: false
      }));
    }

    if (isAddingNew) {
      updatedAddresses?.push(formData);
    } else if (editingIndex !== null) {
      updatedAddresses[editingIndex] = formData;
    }

    setAddresses(updatedAddresses);
    onSave({ addresses: updatedAddresses });
    handleCancel();
  };

  const handleDelete = (index) => {
    let updatedAddresses = addresses?.filter((_, i) => i !== index);
    setAddresses(updatedAddresses);
    onSave({ addresses: updatedAddresses });
  };

  const handleSetPrimary = (index) => {
    let updatedAddresses = addresses?.map((addr, i) => ({
      ...addr,
      isPrimary: i === index
    }));
    setAddresses(updatedAddresses);
    onSave({ addresses: updatedAddresses });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground">
          Delivery Addresses
        </h2>
        {!isAddingNew && editingIndex === null && (
          <Button onClick={handleAddNew} variant="outline">
            <Icon name="Plus" size={16} className="mr-2" />
            Add Address
          </Button>
        )}
      </div>

      {(isAddingNew || editingIndex !== null) && (
        <div className="mb-6 p-6 bg-muted/50 rounded-lg border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {isAddingNew ? 'Add New Address' : 'Edit Address'}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Address Label *
              </label>
              <Input
                type="text"
                name="label"
                value={formData?.label}
                onChange={handleChange}
                placeholder="e.g., Home, Work, Office"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Locality / Street *
              </label>
              <Input
                type="text"
                name="locality"
                value={formData?.locality}
                onChange={handleChange}
                placeholder="Enter locality or street"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  City *
                </label>
                <Input
                  type="text"
                  name="city"
                  value={formData?.city}
                  onChange={handleChange}
                  placeholder="Enter city"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  State/Province *
                </label>
                <Input
                  type="text"
                  name="state"
                  value={formData?.state}
                  onChange={handleChange}
                  placeholder="Enter state"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Pin Code *
                </label>
                <Input
                  type="text"
                  name="pinCode"
                  value={formData?.pinCode}
                  onChange={handleChange}
                  placeholder="Enter pin / postal code"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Country *
                </label>
                <Input
                  type="text"
                  name="country"
                  value={formData?.country}
                  onChange={handleChange}
                  placeholder="Enter country"
                  required
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.isPrimary}
                onChange={(e) => setFormData((prev) => ({ ...prev, isPrimary: e?.target?.checked }))}
                className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-ring"
              />
              <span className="text-sm font-medium text-foreground">
                Set as primary address
              </span>
            </label>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Address'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {addresses?.length === 0 ? (
          <div className="text-center py-12 bg-muted/50 rounded-lg">
            <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No addresses added yet</p>
            <Button onClick={handleAddNew} variant="outline">
              <Icon name="Plus" size={16} className="mr-2" />
              Add Your First Address
            </Button>
          </div>
        ) : (
          addresses?.map((address, index) => (
            <div
              key={index}
              className="p-4 bg-card rounded-lg border border-border hover:shadow-warm transition-smooth"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-foreground">{address?.label}</h3>
                    {address?.isPrimary && (
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                        Primary
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {address?.street}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {address?.city}, {address?.state} {address?.zipCode}
                  </p>
                  <p className="text-sm text-muted-foreground">{address?.country}</p>
                </div>
                <div className="flex items-center gap-2">
                  {!address?.isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(index)}
                      className="p-2 text-muted-foreground hover:text-primary transition-smooth"
                      title="Set as primary"
                    >
                      <Icon name="Star" size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(index)}
                    className="p-2 text-muted-foreground hover:text-foreground transition-smooth"
                    title="Edit address"
                  >
                    <Icon name="Edit" size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="p-2 text-muted-foreground hover:text-destructive transition-smooth"
                    title="Delete address"
                  >
                    <Icon name="Trash2" size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AddressManagementSection;